(() => {
  const historyStateKey = 'luredollsProductDetailRestore';
  const storageKey = 'luredolls_product_detail_restore';
  const restoreMaxAge = 30 * 60 * 1000;
  const restorableDetailsSelector = '.product__accordion details[id], .collapsible-content--faq details[id]';
  const optionRegionSelector = '[data-optionsight-options]';
  const eligibleLinkSelector = 'a[href]';
  const userInterruptEvents = ['pointerdown', 'touchstart', 'wheel', 'keydown'];
  const nativeRestoreSettleDelay = 1800;
  let activeRestoreCleanup = null;
  let activeRestoreCreatedAt = 0;
  let consumedNativeRestoreCreatedAt = 0;
  let deferredClearTimer = 0;
  let manualRestorationPageHideArmed = false;

  function finiteNumber(value, fallback = 0) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function isFresh(target) {
    return Boolean(target && Date.now() - finiteNumber(target.createdAt) < restoreMaxAge);
  }

  function normalizedLinkText(link) {
    return link.textContent.trim().replace(/\s+/g, ' ').slice(0, 160);
  }

  function rootsWithin(root, selector) {
    const roots = [];
    if (root?.matches?.(selector)) roots.push(root);
    root?.querySelectorAll?.(selector).forEach((match) => roots.push(match));
    return roots;
  }

  function optionRegionId(region) {
    return region?.dataset.formId || region?.id || 'product-options';
  }

  function assignLinkAnchors(root = document) {
    rootsWithin(root, restorableDetailsSelector).forEach((details) => {
      details.querySelectorAll('a[href]').forEach((link, index) => {
        link.dataset.productDetailAnchor = `${details.id}:${index}`;
      });
    });

    rootsWithin(root, optionRegionSelector).forEach((region) => {
      const regionId = optionRegionId(region);
      region.querySelectorAll('a[href]').forEach((link, index) => {
        link.dataset.productDetailAnchor = `optionsight:${regionId}:${index}`;
      });
    });
  }

  function regionForLink(link) {
    const details = link.closest(restorableDetailsSelector);
    if (details) {
      return {
        type: details.closest('.collapsible-content--faq') ? 'faq' : 'product-accordion',
        id: details.id,
        root: details,
      };
    }

    const optionRegion = link.closest(optionRegionSelector);
    if (optionRegion) {
      return {
        type: 'optionsight',
        id: optionRegionId(optionRegion),
        root: optionRegion,
      };
    }

    return null;
  }

  function currentHistoryState() {
    return window.history.state && typeof window.history.state === 'object'
      ? window.history.state
      : {};
  }

  function persistTarget(target) {
    consumedNativeRestoreCreatedAt = 0;
    window.clearTimeout(deferredClearTimer);
    try {
      window.history.replaceState(
        {
          ...currentHistoryState(),
          [historyStateKey]: target,
        },
        document.title,
        window.location.href,
      );
    } catch (error) {}

    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(target));
    } catch (error) {}

    window.__luredollsProductDetailRestoreTarget = target;
    // Let Safari paint the cached source position during an interactive back
    // gesture. pagehide then freezes the live BFCache offset so Safari does not
    // apply a second native coordinate restore after the gesture commits.
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'auto';
    deferManualScrollRestorationUntilPageHide();
  }

  function deferManualScrollRestorationUntilPageHide() {
    if (manualRestorationPageHideArmed || !('scrollRestoration' in window.history)) return;

    manualRestorationPageHideArmed = true;
    window.addEventListener('pagehide', () => {
      manualRestorationPageHideArmed = false;
      window.history.scrollRestoration = 'manual';
    }, { once: true });
  }

  function readStoredTarget() {
    const globalTarget = window.__luredollsProductDetailRestoreTarget;
    const historyTarget = currentHistoryState()[historyStateKey];
    let storageTarget = null;

    try {
      const rawTarget = window.sessionStorage.getItem(storageKey);
      storageTarget = rawTarget ? JSON.parse(rawTarget) : null;
    } catch (error) {}

    const target = globalTarget || historyTarget || storageTarget;
    if (!isFresh(target) || target.productPath !== window.location.pathname) return null;
    if (finiteNumber(target.createdAt) === consumedNativeRestoreCreatedAt) return null;
    return target;
  }

  function clearTarget(expectedCreatedAt = 0) {
    try {
      const currentState = currentHistoryState();
      const historyTarget = currentState[historyStateKey];
      if (!expectedCreatedAt || finiteNumber(historyTarget?.createdAt) === expectedCreatedAt) {
        const nextState = { ...currentState };
        delete nextState[historyStateKey];
        window.history.replaceState(nextState, document.title, window.location.href);
      }
    } catch (error) {}

    try {
      const rawTarget = window.sessionStorage.getItem(storageKey);
      const storageTarget = rawTarget ? JSON.parse(rawTarget) : null;
      if (!expectedCreatedAt || finiteNumber(storageTarget?.createdAt) === expectedCreatedAt) {
        window.sessionStorage.removeItem(storageKey);
      }
    } catch (error) {}

    if (
      !expectedCreatedAt
      || finiteNumber(window.__luredollsProductDetailRestoreTarget?.createdAt) === expectedCreatedAt
    ) {
      window.__luredollsProductDetailRestoreTarget = null;
    }
  }

  function deferNativeRestoreCleanup(target) {
    const createdAt = finiteNumber(target?.createdAt);
    if (!createdAt) return;

    consumedNativeRestoreCreatedAt = createdAt;
    window.clearTimeout(deferredClearTimer);

    const clearAfterGestureSettles = () => {
      clearTarget(createdAt);
      if (consumedNativeRestoreCreatedAt === createdAt) consumedNativeRestoreCreatedAt = 0;
      if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'auto';
    };

    deferredClearTimer = window.setTimeout(clearAfterGestureSettles, nativeRestoreSettleDelay);
    window.addEventListener('pagehide', clearAfterGestureSettles, { once: true });
  }

  function isEligibleNavigation(event, link) {
    if (
      event.defaultPrevented ||
      (event.button && event.button !== 0) ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      link.hasAttribute('download') ||
      (link.target && link.target !== '_self')
    ) {
      return false;
    }

    try {
      const destination = new URL(link.href, window.location.href);
      const current = new URL(window.location.href);
      const isHttpNavigation = destination.protocol === 'http:' || destination.protocol === 'https:';
      const isSameDocument = destination.origin === current.origin
        && destination.pathname === current.pathname
        && destination.search === current.search;

      return isHttpNavigation
        && !isSameDocument
        && !destination.hash.startsWith('#ProductAccordion-');
    } catch (error) {
      return false;
    }
  }

  function rememberLinkNavigation(event) {
    const targetElement = event.target instanceof Element ? event.target : null;
    const link = targetElement?.closest(eligibleLinkSelector);
    if (!link || !isEligibleNavigation(event, link)) return;

    const region = regionForLink(link);
    if (!region) return;

    assignLinkAnchors(region.root);
    const links = Array.from(region.root.querySelectorAll('a[href]'));
    const details = region.root.matches('details[id]') ? region.root : null;
    const linkRect = link.getBoundingClientRect();
    const maximumViewportTop = Math.max(window.innerHeight - Math.min(linkRect.height, window.innerHeight), 0);
    const viewportTop = Math.min(Math.max(finiteNumber(linkRect.top), 0), maximumViewportTop);
    const openDetailsIds = Array.from(document.querySelectorAll(restorableDetailsSelector))
      .filter((openDetails) => openDetails.open)
      .map((openDetails) => openDetails.id)
      .filter(Boolean);

    persistTarget({
      version: 2,
      productPath: window.location.pathname,
      regionType: region.type,
      regionId: region.id,
      detailsId: details?.id || '',
      openDetailsIds,
      anchorToken: link.dataset.productDetailAnchor || `${region.type}:${region.id}:${links.indexOf(link)}`,
      linkIndex: links.indexOf(link),
      linkHref: link.href,
      linkText: normalizedLinkText(link),
      viewportTop: Math.round(viewportTop),
      scrollY: Math.round(window.scrollY || 0),
      documentHeight: Math.max(
        document.documentElement.scrollHeight || 0,
        Math.round((window.scrollY || 0) + window.innerHeight),
      ),
      createdAt: Date.now(),
    });
  }

  function openSavedDetails(target) {
    const ids = Array.isArray(target.openDetailsIds) && target.openDetailsIds.length
      ? target.openDetailsIds
      : [target.detailsId];

    ids.forEach((id) => {
      const details = document.getElementById(id);
      if (details?.matches(restorableDetailsSelector)) details.open = true;
    });

    const primaryDetails = document.getElementById(target.detailsId);
    if (primaryDetails?.matches(restorableDetailsSelector)) primaryDetails.open = true;
    return primaryDetails;
  }

  function findRegion(target, primaryDetails) {
    if (primaryDetails) return primaryDetails;
    if (target.regionType !== 'optionsight') return null;

    return Array.from(document.querySelectorAll(optionRegionSelector)).find(
      (region) => optionRegionId(region) === target.regionId,
    ) || null;
  }

  function isRenderedAnchor(link) {
    return Boolean(link?.isConnected && link.getClientRects().length);
  }

  function findAnchor(target, region) {
    if (!region) return null;

    const tokenMatch = Array.from(region.querySelectorAll('a[href]')).find(
      (link) => link.dataset.productDetailAnchor === target.anchorToken,
    );
    if (isRenderedAnchor(tokenMatch)) return tokenMatch;

    const links = Array.from(region.querySelectorAll('a[href]'));
    const indexedLink = links[target.linkIndex];
    if (
      isRenderedAnchor(indexedLink)
      && (!target.linkHref || indexedLink.href === target.linkHref)
    ) {
      return indexedLink;
    }

    return links.find((link) => (
      isRenderedAnchor(link)
      && (!target.linkHref || link.href === target.linkHref)
      && (!target.linkText || normalizedLinkText(link) === target.linkText)
    )) || null;
  }

  function finishRestore({ clear = true, restoreScrollRestoration = true } = {}) {
    activeRestoreCleanup?.();
    activeRestoreCleanup = null;
    activeRestoreCreatedAt = 0;
    window.__luredollsStopProductDetailPrepaint?.();
    window.__luredollsStopProductDetailPrepaint = null;
    document.documentElement.classList.remove('product-detail-restore-pending');
    document.documentElement.classList.remove('product-detail-restore-before-first-align');
    document.documentElement.style.removeProperty('--product-detail-restore-min-height');
    if (restoreScrollRestoration && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'auto';
    }
    if (clear) clearTarget();
  }

  function alignCachedStaticAnchor(target) {
    const details = openSavedDetails(target);
    const region = findRegion(target, details);
    if (region) assignLinkAnchors(region);
    const anchor = findAnchor(target, region);

    if (anchor) {
      const desiredTop = finiteNumber(target.viewportTop, Math.min(window.innerHeight * 0.35, 280));
      const delta = anchor.getBoundingClientRect().top - desiredTop;
      if (Math.abs(delta) > 1) window.scrollBy(0, delta);
      return;
    }

    if (finiteNumber(target.scrollY, 0) > 0) {
      window.scrollTo(0, finiteNumber(target.scrollY, 0));
    }
  }

  function restoreTarget(target, { persisted = false } = {}) {
    activeRestoreCleanup?.();
    window.__luredollsStopProductDetailPrepaint?.();
    window.__luredollsStopProductDetailPrepaint = null;
    document.documentElement.classList.add('product-detail-restore-pending');
    if (target.regionType === 'optionsight') {
      document.documentElement.classList.add('product-detail-restore-before-first-align');
    }
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';

    const desiredTop = finiteNumber(target.viewportTop, Math.min(window.innerHeight * 0.35, 280));
    const startedAt = performance.now();
    const minimumLockDuration = persisted ? 1200 : 1800;
    const maximumLockDuration = persisted ? 2400 : 3200;
    let lastCorrectionAt = startedAt;
    let correctionFrame = 0;
    let stopped = false;
    let resizeObserver = null;
    let mutationObserver = null;
    let finishTimer = 0;
    let anchorFound = false;

    const removeInterruptListeners = () => {
      userInterruptEvents.forEach((eventName) => window.removeEventListener(eventName, interruptRestore));
    };

    const cleanup = () => {
      if (stopped) return;
      stopped = true;
      window.cancelAnimationFrame(correctionFrame);
      window.clearTimeout(finishTimer);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      removeInterruptListeners();
    };

    const complete = () => {
      cleanup();
      activeRestoreCleanup = null;
      activeRestoreCreatedAt = 0;
      document.documentElement.classList.remove('product-detail-restore-pending');
      document.documentElement.classList.remove('product-detail-restore-before-first-align');
      document.documentElement.style.removeProperty('--product-detail-restore-min-height');
      if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'auto';
      clearTarget();
    };

    function interruptRestore() {
      complete();
    }

    const correctAnchor = () => {
      correctionFrame = 0;
      if (stopped) return;

      const details = openSavedDetails(target);
      const region = findRegion(target, details);
      if (region) assignLinkAnchors(region);
      const anchor = findAnchor(target, region);

      if (anchor) {
        anchorFound = true;
        const delta = anchor.getBoundingClientRect().top - desiredTop;
        if (Math.abs(delta) > 1) {
          window.scrollBy(0, delta);
          lastCorrectionAt = performance.now();
        }

        document.documentElement.classList.remove('product-detail-restore-before-first-align');
      }

      const elapsed = performance.now() - startedAt;
      const quietFor = performance.now() - lastCorrectionAt;
      if (elapsed >= maximumLockDuration || (anchorFound && elapsed >= minimumLockDuration && quietFor >= 600)) {
        complete();
        return;
      }

      scheduleCorrection();
    };

    const scheduleCorrection = () => {
      if (stopped || correctionFrame) return;
      correctionFrame = window.requestAnimationFrame(correctAnchor);
    };

    const correctAfterLayoutChange = () => {
      if (stopped) return;
      if (correctionFrame) window.cancelAnimationFrame(correctionFrame);
      correctionFrame = 0;
      correctAnchor();
    };

    userInterruptEvents.forEach((eventName) => {
      window.addEventListener(eventName, interruptRestore, { once: true, passive: true });
    });

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(correctAfterLayoutChange);
      resizeObserver.observe(document.body);
      const mainContent = document.getElementById('MainContent');
      if (mainContent) resizeObserver.observe(mainContent);
    }

    if ('MutationObserver' in window) {
      mutationObserver = new MutationObserver(correctAfterLayoutChange);
      mutationObserver.observe(document.getElementById('MainContent') || document.body, {
        childList: true,
        subtree: true,
      });
    }

    finishTimer = window.setTimeout(complete, maximumLockDuration + 100);
    activeRestoreCleanup = cleanup;
    activeRestoreCreatedAt = target.createdAt;

    correctAnchor();
  }

  assignLinkAnchors();
  document.addEventListener('click', rememberLinkNavigation, { capture: true });
  document.addEventListener('shopify:section:load', (event) => assignLinkAnchors(event.target));

  function restoreStoredTarget({ persisted = false } = {}) {
    const target = readStoredTarget();
    if (!target) return;
    if (activeRestoreCleanup && activeRestoreCreatedAt === target.createdAt) return;

    // The cached static accordion already contains its saved link, but pagehide
    // switched this history entry to manual restoration. Align that semantic
    // anchor once during pageshow so taller Safari viewports cannot remain at
    // scrollY zero. Avoid the multi-frame lock here because repeated scrolls can
    // invalidate a visible WebKit tile while the back gesture is committing.
    if (persisted && target.regionType !== 'optionsight') {
      alignCachedStaticAnchor(target);
      // Do not call replaceState while Safari is still committing the
      // interactive back gesture. On some iPhones that resets the native
      // position after the correct frame has already been painted.
      finishRestore({ clear: false, restoreScrollRestoration: false });
      deferNativeRestoreCleanup(target);
      return;
    }

    restoreTarget(target, { persisted });
  }

  window.addEventListener('pageshow', (event) => {
    restoreStoredTarget({ persisted: event.persisted });
  });

  const initialTarget = readStoredTarget();
  if (initialTarget && document.documentElement.classList.contains('product-detail-restore-pending')) {
    restoreStoredTarget({ persisted: false });
  }
})();
